import { clone, isEqual } from "lodash";
import { useReducer, useEffect, JSX } from "react";
import { ResourceFiltrationService } from "@app/projects/resource-filtration";
import { FilteredResource, ResourceType } from "@app/resources";
import { UserInfo } from "@app/user/UserContext";
import { ActiveFilters } from "@app/resource-filters/ActiveFilters";
import { FilterItemsGroupOperation, FilterItemType } from "@app/resource-filters/common";
import { CreateFilter } from "@app/resource-filters/CreateFilter";
import { FilterItem, Filter, FilterItemsGroup } from "@app/resource-filters/filter-items";
import { FilterSet } from "@app/resource-filters/filter-sets/filterSet";
import { ResourceFilterSet } from "@app/resource-filters/filter-sets/resourceFilterSet";

interface FilterControlsProps {
  newFilterKeys: FilterItem[];
  resourceType: ResourceType;
  user: UserInfo;
  /** Called when the filtered resources changed, outputs full collection of filtered resource ids each time */
  onFilteredItemsChange?: (resources: FilteredResource[]) => void;
}

interface FilterControlsState {
  activeFilter: Filter;
  activeFilterSet: ResourceFilterSet;
  // Calling it 'context'Status bc I'm thinking this might make sense to use react context for this when we have time
  contextStatus: "busy" | "ready";
  /**
   * This is the navigation history of the filters and filter-items NOT a cache...this object cannot work that way the UI functionality
   * relies on it being filled up and emptied out. Not saying we can't cache the filter-items...just that this is not the place
   */
  filterHistory: Filter[];
  filterSets: ResourceFilterSet[];
  resourceType: ResourceType;
}

/** The set of possible actions that can occur within the FilterControls. */
type ActionType =
  | { type: "active-filter-changed", activeFilter: Filter, filterHistory: Filter[] }
  | { type: "edit-filter-canceled", activeFilter: Filter, filterHistory: Filter[] }
  | { type: "filter-edited", activeFilter: Filter, filterHistory: Filter[] }
  | { type: "filter-removed", activeFilterSet: ResourceFilterSet }
  | { type: "filter-saved", activeFilter: Filter, activeFilterSet: ResourceFilterSet, filterHistory: Filter[] }
  | { type: "filter-set-changed", activeFilterSet: ResourceFilterSet }
  | { type: "loading" }
  | { type: "loading-complete" }
  | { type: "loading-state" }
  | { type: "loading-state-complete", activeFilterSet: ResourceFilterSet, filterSets: ResourceFilterSet[] }
  | { type: "pending-filter-removed", activeFilterSet: ResourceFilterSet }
  | { type: "reset", resourceType: ResourceType };

/**
 * Given a state and an action that has occurred, produces a new state.
 * @param state The current state of the FilterControls.
 * @param action The action that occurred.
 */
const reducer = (state: FilterControlsState, action: ActionType) => {
  switch (action.type) {
    case "active-filter-changed": return { ...state, ...action };
    case "edit-filter-canceled": return { ...state, ...action };
    case "filter-edited": return { ...state, ...action };
    case "filter-removed": return { ...state, ...action };
    case "filter-saved": return { ...state, ...action };
    case "filter-set-changed": return { ...state, ...action };
    case "loading": return { ...state, contextStatus: "busy" };
    case "loading-complete": return { ...state, contextStatus: "ready" };
    case "loading-state": return { ...state, contextStatus: "busy" };
    case "loading-state-complete": return { ...state, contextStatus: "ready", ...action };
    case "pending-filter-removed": return { ...state, ...action };
    case "reset": return defaultState(action.resourceType);
  }
};

//const projectService = new ProjectService();
const resourceFiltrationService = new ResourceFiltrationService();

/**
 * Creates a default filter set for a given type.
 * @param resourceType The resource type to create the default for.
 */
const defaultFilterSet: (resourceType: ResourceType) => ResourceFilterSet = (resourceType) => ({
  filterGroups: [],
  filterSetId: -1,
  filterSetType: "default",
  groupsOperation: FilterItemsGroupOperation.LogicalAnd,
  name: resourceTypeDisplayName(resourceType),
  readonly: true,
  targetResourceType: resourceType,
});

/** The default, minimally initialized filter. */
const defaultFilter: Filter = { branches: [], filterPath: "", leaves: [] };

/**
 * Creates a default state for a given resource type.
 * @param resourceType The resource type to produce a default state for.
 */
const defaultState: (resourceType: ResourceType) => FilterControlsState = (resourceType) => ({
  activeFilter: defaultFilter,
  activeFilterSet: defaultFilterSet(resourceType),
  contextStatus: "busy",
  filterHistory: [],
  filterSets: [],
  resourceType,
});

/**
 * Produces the storage key for a given resource type and authenticated user.
 * @param resourceType The resource type.
 * @param user The current user.
 */
function storageKey(resourceType: ResourceType, user: UserInfo) {
  return `${user.userId}${resourceType}_filters`;
}

/**
 * Retrieves from local storage the last set of filters. If no value is found then it returns a simple default.
 * @param resourceType The resource type to retrieve the filters for.
 * @param user The relevant user's account.
 */
function loadFromStorage(resourceType: ResourceType, user: UserInfo) {
  let activeFilterSet = defaultFilterSet(resourceType);
  const storedFilterSet = localStorage.getItem(storageKey(resourceType, user));
  if (storedFilterSet) {
    activeFilterSet = JSON.parse(storedFilterSet);
  }

  return activeFilterSet;
}

/**
 * Retrieves the applicable identifiers of the given projects.
 * Caveat: this is only for Network Assessment (CustomProject) projects.
 * @param filterSet The filter set used for filtering.
 */
async function loadFilteredProjectResources(filterSet: ResourceFilterSet) {
  if (!filterSet) {
    return new Array<FilteredResource>();
  }

  const items = [...(filterSet.filterGroups || []), ...(filterSet.pendingChanges || [])];
  const projectResources = await resourceFiltrationService.getFilteredProjectResources(items);

  return projectResources;
}

/**
 * Renders the filter control section of the user interface.
 * Manages the data associated with the filters and notifies consuming components of the filtered resource identifiers.
 */
function FilterControls({ newFilterKeys, resourceType, user, onFilteredItemsChange }: FilterControlsProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, resourceType, defaultState);

  // Whenever our resource type changes, reset the state accordingly.
  useEffect(() => {
    if (resourceType !== state.resourceType) {
      dispatch({ resourceType, type: "reset" });
    }
  }, [resourceType, state.resourceType]);

  // Load our initial state from local storage, retrieve the project identifiers, and notify accordingly.
  // This occurs only once at startup, for the moment.
  useEffect(() => {
    dispatch({ type: "loading-state" });

    const filterSets = [defaultFilterSet(resourceType)];
    const filterSet = loadFromStorage(resourceType, user);
    loadFilteredProjectResources(filterSet).then((projectResources) => {
      if (onFilteredItemsChange) {
        onFilteredItemsChange(projectResources);
      }

      dispatch({ activeFilterSet: filterSet, filterSets, type: "loading-state-complete" });
    }).catch(() => {
      // TODO: Handle the error.
      dispatch({ activeFilterSet: state.activeFilterSet, filterSets: [], type: "loading-state-complete" });
      localStorage.removeItem(storageKey(resourceType, user));
    });
  }, []);

  useEffect(() => {
    const activeFilters: FilterItem[] = [];
    if (state.activeFilterSet.filterGroups) {
      state.activeFilterSet.filterGroups.forEach((group) => activeFilters.push(group.filters[0] as FilterItem));
    }
    
    if (state.activeFilterSet.pendingChanges) {
      state.activeFilterSet.pendingChanges.forEach((change) => activeFilters.push(change.filters[0] as FilterItem));
    }

    // Each of these should only have 1 item in their arrays
    if (!activeFilters.some((af) => af && af.namespacePath === newFilterKeys[0].namespacePath)) {
      const newFilters: FilterItem[] = [];
      newFilterKeys.forEach((filter) => {
        if (!state.activeFilter.leaves.includes(filter)) {
          newFilters.push(filter);
        }
      });

      handleSaveFilter(newFilters);
    }
  }, [newFilterKeys]);

  // Event Handlers
  /* eslint-disable-next-line space-before-function-paren -- false positive */
  function busyWrapper<T extends (...args: unknown[]) => Promise<unknown>>(func: T): (...funcArgs: Parameters<T>) => ReturnType<T> {
    return (...args: Parameters<T>): ReturnType<T> => {
      if (state.contextStatus !== "busy") {
        dispatch({ type: "loading" });

        const res = Promise.resolve(func(...args)).then((val) => {
          dispatch({ type: "loading-complete" });
          return val;
        }).catch(() => {
          // TODO: Error type.
          dispatch({ type: "loading-complete" });
        });

        return res as ReturnType<T>;
      }
    };
  }

  const handleFilterSetChange = busyWrapper(async (filterSet: FilterSet) => {
    const activeFilterSet: ResourceFilterSet = {
      filterGroups: filterSet.filterGroups,
      filterSetId: filterSet.filterSetId,
      filterSetType: "saved",
      groupsOperation: FilterItemsGroupOperation.LogicalAnd,
      name: filterSet.name,
      pendingChanges: [],
      readonly: false,
      targetResourceType: resourceType,
    };

    const projectResources = await resourceFiltrationService.getFilteredProjectResources(activeFilterSet.filterGroups);
    if (onFilteredItemsChange) {
      onFilteredItemsChange(projectResources);
    }

    dispatch({ activeFilterSet, type: "filter-set-changed" });
    localStorage.setItem(storageKey(resourceType, user), JSON.stringify(activeFilterSet));
  });

  const handleFilterItemSelectionChange = busyWrapper(async (selected: boolean, filterItem?: FilterItem) => {
    let filter: Filter = selected && filterItem
      ? await buildActiveFilter(filterItem, state.activeFilterSet)
      : undefined;

    const history = [...state.filterHistory];
    if (filter) {
      history.push(filter);
    } else if (history.length > 1) {
      history.pop();
      filter = history[history.length - 1];
    }

    if (filter) {
      dispatch({ activeFilter: filter, filterHistory: history, type: "active-filter-changed" });
    }
  });

  const handleRemoveFilterGroup = busyWrapper(async (filterGroup: FilterItemsGroup) => {
    //TODO: MODIFY THE WAY WE'RE TRACKING PENDING CHANGES SO THAT
    //THIS IS IDENTIFIED AS A PENDING CHANGE AS WELL...THIS IS A PENDING REMOVAL
    //TODO: THIS WILL NEED TO GET THE UPDATED LIST OF PROJECTIDS AND OUTPUT THE RESULTS ON THE EVENT HANDLER
    const activeFilterSet = clone(state.activeFilterSet);
    activeFilterSet.filterGroups = activeFilterSet.filterGroups
      .filter((fsi) => !isEqual(fsi, filterGroup));

    const filters = activeFilterSet.pendingChanges
      ? [...activeFilterSet.filterGroups, ...activeFilterSet.pendingChanges]
      : [...activeFilterSet.filterGroups];

    const projectResources = await resourceFiltrationService.getFilteredProjectResources(filters);
    if (onFilteredItemsChange) {
      onFilteredItemsChange(projectResources);
    }

    localStorage.setItem(storageKey(resourceType, user), JSON.stringify(activeFilterSet));
    dispatch({ activeFilterSet, type: "filter-removed" });
  });

  const handleRemovePendingFilterGroup = busyWrapper(async (filterGroup: FilterItemsGroup) => {
    const activeFilterSet = clone(state.activeFilterSet);
    activeFilterSet.pendingChanges = activeFilterSet.pendingChanges
      .filter((fsi) => !isEqual(fsi, filterGroup));

    const filters = activeFilterSet.pendingChanges
      ? [...activeFilterSet.filterGroups, ...activeFilterSet.pendingChanges]
      : [...activeFilterSet.filterGroups];

    const projectResources = await resourceFiltrationService.getFilteredProjectResources(filters);
    if (onFilteredItemsChange) {
      onFilteredItemsChange(projectResources);
    }

    localStorage.setItem(storageKey(resourceType, user), JSON.stringify(activeFilterSet));
    dispatch({ activeFilterSet, type: "pending-filter-removed" });
  });

  const handleSaveFilter = busyWrapper(async (leafFilterItems: FilterItem[]) => {
    const activeFilterSet = clone(state.activeFilterSet);

    if (activeFilterSet.filterSetType === "default") {
      activeFilterSet.name = "Untitled";
      activeFilterSet.filterSetType = "new";
    }

    const filterGroup: FilterItemsGroup = {
      filters: leafFilterItems,
      operation: FilterItemsGroupOperation.LogicalOr,
    };

    activeFilterSet.pendingChanges = activeFilterSet.pendingChanges
      ? [...activeFilterSet.pendingChanges, filterGroup]
      : [filterGroup];

    const projectResources = await resourceFiltrationService.getFilteredProjectResources(
      [...activeFilterSet.filterGroups, ...activeFilterSet.pendingChanges]);

    if (onFilteredItemsChange) {
      onFilteredItemsChange(projectResources);
    }

    localStorage.setItem(storageKey(resourceType, user), JSON.stringify(activeFilterSet));
    dispatch({ activeFilter: defaultFilter, activeFilterSet, filterHistory: [], type: "filter-saved" });
  });

  const handleWillCancelEditFilterItem = busyWrapper(async () => {
    const history: Filter[] = state.filterHistory.length > 0
      ? [state.filterHistory[0]]
      : [];

    const filter: Filter = history.length === 1
      ? history[0]
      : defaultFilter;

    dispatch({ activeFilter: filter, filterHistory: history, type: "edit-filter-canceled" });
  });

  const handleWillEditFilterItem = busyWrapper(async () => {
    // TODO: Change this so we can ID if we already tried to get these from the server and it returned and empty array
    if (state.filterHistory.length === 0) {
      const filter = await buildActiveFilter(undefined, state.activeFilterSet);

      const history = [...state.filterHistory, filter];

      dispatch({ activeFilter: filter, filterHistory: history, type: "filter-edited" });
    }
  });

  return (
    <div className="row">
      <div className="col-12 filter-controls-wrapper">
        <ul className="filter-controls">
          <ActiveFilters
            filterGroups={state.activeFilterSet.filterGroups}
            pendingFilterGroups={state.activeFilterSet.pendingChanges}
            onRemoveFilterGroup={handleRemoveFilterGroup}
            onRemovePendingFilterGroup={handleRemovePendingFilterGroup}
          />
          <CreateFilter
            activeFilter={state.activeFilter}
            activeFilterSet={state.activeFilterSet}
            canEdit={state.contextStatus === "ready"}
            hasFilters={state.activeFilterSet.filterGroups.length > 0 || (!!state.activeFilterSet.pendingChanges && !!(state.activeFilterSet.pendingChanges.length > 0))}
            onFilterSetChange={handleFilterSetChange}
            onSaveFilter={handleSaveFilter}
            onSelectionChange={handleFilterItemSelectionChange}
            onWillCancelEdit={handleWillCancelEditFilterItem}
            onWillEdit={handleWillEditFilterItem}
            resourceType={resourceType}
          />
        </ul>
      </div>
    </div>
  );
}

/**
 * Standard way to retrieves filter...will propagate errors when encountered
 * @param projectService The project-service
 * @param currentFilter [Optional] The current filter
 */
async function buildActiveFilter(currentFilter?: FilterItem, currentFilterSet?: ResourceFilterSet): Promise<Filter> {
  let filterItems: FilterItem[];

  if (currentFilterSet && (currentFilterSet.filterGroups.length > 0 || (currentFilterSet.pendingChanges && currentFilterSet.pendingChanges.length > 0))) {
    filterItems = await resourceFiltrationService.getFilterItemsInScope([...currentFilterSet.filterGroups, ...currentFilterSet.pendingChanges], currentFilter ? currentFilter.namespacePath : undefined);
  } else {
    filterItems = await resourceFiltrationService.getFilterItems(currentFilter ? currentFilter.namespacePath : undefined);
  }

  const activeFilter: Filter = {
    branches: [],
    filterPath: currentFilter && currentFilter.namespacePath || "",
    leaves: [],
  };

  filterItems.forEach((fi) => {
    switch (fi.itemType) {
      case FilterItemType.Branch:
        activeFilter.branches.push(fi);
        break;
      case FilterItemType.Leaf:
        activeFilter.leaves.push(fi);
        break;
      default:
        break;
    }
  });

  return activeFilter;
}

/**
 * Returns the placeholder text to display in the filter-set drop down when no filterset is selected
 * @param resourceType
 */
function resourceTypeDisplayName(resourceType: ResourceType): string {
  switch (resourceType) {
    case ResourceType.ZipAnalysisProject:
    case ResourceType.CustomProject:
    case ResourceType.Project:
      return "All Projects";
    case ResourceType.AdequacyZipTemplateCollection:
      return "All Zip Templates"
    case ResourceType.AdequacyTemplateCollection:
      return "All County Templates";
    case ResourceType.Category:
      return "All Categories";
    case ResourceType.CensusFile:
      return "All Censuses";
    case ResourceType.FilteredProviderSet:
      return "All Filtered Networks";
    case ResourceType.Folder:
      return "All Folders";
    case ResourceType.MemberSet:
      return "All Member Populations";
    case ResourceType.ProviderSet:
      return "All Networks";
    case ResourceType.SpecialtyList:
      return "All Specialty Lists";
    case ResourceType.Unknown:
      return "Unknown";
  }
}

export { FilterControls as FilterControls };
